import { Component, Inject, OnDestroy } from "@angular/core";
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule, FormsModule } from "@angular/forms";
import { HttpClient } from "@angular/common/http";
import { Subject } from "rxjs";
import { finalize, takeUntil } from "rxjs/operators";
import { DialogService } from "../../../services/dialog.service";
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTableModule } from '@angular/material/table';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { NgClass } from '@angular/common';
// Mirror backend enums (values must match StringEnumConverter output)
type KeyVaultSecretStatus = 'Active' | 'Deleted' | 'NotFound' | 'Forbidden' | 'Error';
type KeyVaultOperationStatus = 'Success' | 'NotFound' | 'AlreadyDeleted' | 'Forbidden' | 'Conflict' | 'Error';

interface KeyVaultManagementRequest {
    VaultName: string;
    PkiRegion: string;
    MacAddress: string;
    UnitId: string;
}

interface KeyVaultSecretStatusResult {
    SecretName: string;
    Status: KeyVaultSecretStatus;
    Detail?: string;
    TechnicalError?: string;
}

interface KeyVaultSecretOperationResult {
    SecretName: string;
    DeleteStatus: KeyVaultOperationStatus;
    DeleteDetail?: string;
    PurgeStatus: KeyVaultOperationStatus;
    PurgeDetail?: string;
}

@Component({
    selector: "keyvaultmanagement",
    standalone: true,
    imports: [
        ReactiveFormsModule,
        FormsModule,
        MatFormFieldModule,
        MatInputModule,
        MatSelectModule,
        MatButtonModule,
        MatIconModule,
        MatTableModule,
        MatButtonToggleModule,
        MatProgressBarModule,
        MatTooltipModule,
        NgClass
    ],
    templateUrl: './keyvaultmanagement.component.html',
    styleUrls: ['../../../style/container-tab-list.component.less', './keyvaultmanagement.component.less']
})
export class KeyVaultManagementComponent implements OnDestroy {

    title = "Key Vault Certificate Management";
    isLocked = true;

    form: FormGroup;
    searchResults: KeyVaultSecretStatusResult[] = [];
    operationResults: KeyVaultSecretOperationResult[] = [];
    isLoading = false;
    resultMessage = '';
    isResultMessageError = false;
    technicalErrorMessage = '';
    showTechnicalDetails = false;
    readonly vaultNames: string[] = [
        'StableEmtEquipmentCerts',
        'DevEmtEquipmentCerts',
        'EAEmtEquipmentCerts',
        'AMSEmtEquipmentCerts',
        'APEmtEquipmentCerts'
    ];

    private readonly destroy$ = new Subject<void>();

    private readonly statusMessages: Record<KeyVaultSecretStatus, string> = {
        Active:   'Found (active)',
        Deleted:  'Deleted (purgeable)',
        NotFound: 'Not found',
        Forbidden:'Permission denied',
        Error:    'Error'
    };

    private readonly operationMessages: Record<KeyVaultOperationStatus, (action: string) => string> = {
        Success:      (a) => `${a} successful.`,
        NotFound:     (a) => `Secret not found for ${a.toLowerCase()}.`,
        AlreadyDeleted: (_) => 'Already deleted.',
        Forbidden:    (a) => `Permission denied during ${a.toLowerCase()}.`,
        Conflict:     (a) => `${a} temporarily unavailable. Try again.`,
        Error:        (a) => `${a} failed.`
    };

    constructor(
        private readonly fb: FormBuilder,
        private readonly http: HttpClient,
        private readonly confirmDialog: DialogService,
        @Inject('BASE_URL') private readonly baseUrl: string
    ) {
        const defaultVaultName = this.vaultNames[0];

        this.form = this.fb.group({
            VaultName: [defaultVaultName],
            PkiRegion: ['ea',
                [Validators.required, Validators.maxLength(10)]],
            MacAddress: ['',
                [Validators.required, Validators.pattern(/^([0-9A-Fa-f]{2}[:-]){5}[0-9A-Fa-f]{2}$/)]],
            UnitId: ['',
                [Validators.required, Validators.maxLength(50)]]
        });

        // Clear stale results whenever the form value changes
        this.form.valueChanges
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => {
                if (this.searchResults.length || this.operationResults.length || this.resultMessage) {
                    this.clearResults();
                }
            });

        this.updateLockedControlsState();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    get secretBaseName(): string {
        const raw = this.form.getRawValue();
        const mac  = (raw.MacAddress ?? '').replaceAll(':', '-');
        const unit = (raw.UnitId     ?? '').replaceAll('_', '-');
        return `MAX-${raw.PkiRegion ?? ''}-${mac}-${unit}`.toUpperCase();
    }

    toggleEditMode(): void {
        this.isLocked = !this.isLocked;
        this.updateLockedControlsState();
    }

    private updateLockedControlsState(): void {
        // Reactive Forms ignores the template `[disabled]` binding on controls bound via
        // formControlName, so the disabled state must be set on the FormControl itself.
        const vaultNameControl = this.form.get('VaultName');
        const pkiRegionControl = this.form.get('PkiRegion');

        if (this.isLocked) {
            vaultNameControl?.disable({ emitEvent: false });
            pkiRegionControl?.disable({ emitEvent: false });
        } else {
            vaultNameControl?.enable({ emitEvent: false });
            pkiRegionControl?.enable({ emitEvent: false });
        }
    }

    onSearch(): void {
        if (this.form.invalid) { return; }
        this.clearResults();
        this.isLoading = true;

        this.http.post<KeyVaultSecretStatusResult[]>(
            this.baseUrl + 'api/keyvaultmanagement/search',
            this.getRequestPayload()
        ).pipe(
            finalize(() => this.isLoading = false),
            takeUntil(this.destroy$)
        ).subscribe({
            next:  res => {
                this.searchResults = res;
                this.isResultMessageError = false;
            },
            error: err => {
                const searchResults = this.tryReadSearchResultsFromError(err?.error);
                if (searchResults) {
                    this.searchResults = searchResults;

                    if (err?.status === 403) {
                        this.resultMessage = 'Search failed due to insufficient permissions.';
                    } else if (err?.status === 500) {
                        this.resultMessage = 'Search completed with internal errors. Please review the result details.';
                    } else {
                        this.resultMessage = 'Search failed.';
                    }
                } else {
                    this.resultMessage = 'Search failed.';
                }

                this.isResultMessageError = true;
                this.technicalErrorMessage = this.extractErrorMessage(err);
            }
        });
    }

    onDeleteConfirm(): void {
        this.confirmDialog.showConfirmDialog(
            'Are you sure you want to delete and purge the secrets?',
            'Confirm Delete',
            this
        );
    }

    onBack() { }

    onYes(): void {
        this.clearResults();
        this.isLoading = true;

        this.http.post<KeyVaultSecretOperationResult[]>(
            this.baseUrl + 'api/keyvaultmanagement/delete',
            this.getRequestPayload()
        ).pipe(
            finalize(() => this.isLoading = false),
            takeUntil(this.destroy$)
        ).subscribe({
            next: res => {
                this.operationResults = res;
                this.resultMessage = 'Process completed.';
                this.isResultMessageError = false;
            },
            error: err => {
                const operationResults = this.tryReadOperationResultsFromError(err?.error);
                if (operationResults) {
                    this.operationResults = operationResults;

                    if (err?.status === 403) {
                        this.resultMessage = 'Delete and purge failed due to insufficient permissions.';
                    } else if (err?.status === 500) {
                        this.resultMessage = 'Delete and purge completed with internal errors. Please review operation details.';
                    } else {
                        this.resultMessage = 'Delete and purge failed. Please verify your access and try again.';
                    }
                } else {
                    this.resultMessage = 'Delete and purge failed. Please verify your access and try again.';
                }

                this.isResultMessageError = true;
                this.technicalErrorMessage = this.extractErrorMessage(err);
            }
        });
    }

    toggleTechnicalDetails(): void {
        this.showTechnicalDetails = !this.showTechnicalDetails;
    }

    getStatusMessage(status: KeyVaultSecretStatus): string {
        return this.statusMessages[status] ?? status;
    }

    getOperationMessage(status: KeyVaultOperationStatus, action: string): string {
        return this.operationMessages[status]?.(action) ?? `${action}: ${status}`;
    }

    isStatusError(status: KeyVaultSecretStatus): boolean {
        return status === 'Error' || status === 'Forbidden';
    }

    isOperationError(status: KeyVaultOperationStatus): boolean {
        return status === 'Error' || status === 'Forbidden';
    }

    hasOperationDetail(status: KeyVaultOperationStatus, detail?: string): boolean {
        return !!detail;
    }

    private clearResults(): void {
        this.searchResults = [];
        this.operationResults = [];
        this.resultMessage = '';
        this.isResultMessageError = false;
        this.technicalErrorMessage = '';
        this.showTechnicalDetails = false;
    }

    private getRequestPayload(): KeyVaultManagementRequest {
        const raw = this.form.getRawValue();

        return {
            VaultName: raw.VaultName,
            PkiRegion: raw.PkiRegion,
            MacAddress: raw.MacAddress,
            UnitId: raw.UnitId
        };
    }

    private tryReadOperationResultsFromError(payload: any): KeyVaultSecretOperationResult[] | null {
        if (!Array.isArray(payload)) {
            return null;
        }

        if (payload.every(item => item && typeof item.SecretName === 'string' && item.DeleteStatus && item.PurgeStatus)) {
            return payload as KeyVaultSecretOperationResult[];
        }

        return null;
    }

    private tryReadSearchResultsFromError(payload: any): KeyVaultSecretStatusResult[] | null {
        if (!Array.isArray(payload)) {
            return null;
        }

        if (payload.every(item => item && typeof item.SecretName === 'string' && item.Status)) {
            return payload as KeyVaultSecretStatusResult[];
        }

        return null;
    }

    private extractErrorMessage(error: any): string {
        if (Array.isArray(error?.error)) {
            const searchDetails = (error.error as KeyVaultSecretStatusResult[])
                .flatMap(r => [r.Detail, r.TechnicalError])
                .filter((d): d is string => !!d && d.trim().length > 0);

            if (searchDetails.length > 0) {
                return searchDetails.join(' | ');
            }

            const operationDetails = (error.error as KeyVaultSecretOperationResult[])
                .flatMap(r => [r.DeleteDetail, r.PurgeDetail])
                .filter((d): d is string => !!d && d.trim().length > 0);

            if (operationDetails.length > 0) {
                return operationDetails.join(' | ');
            }

            return 'Operation returned an error status.';
        }

        if (error?.error?.errors) {
            return (Object.values(error.error.errors)).flat().join(' ');
        }
        return (error?.error?.Error ?? error?.error?.title ?? error?.message ?? 'Unknown error').toString();
    }
}

